import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { faSearch } from "@fortawesome/pro-light-svg-icons";
import { faRankingStar } from "@fortawesome/pro-regular-svg-icons";
import { useSelector } from "react-redux";
import IconWithBackground from "../IconWithBackground";
import { showTranslated, translate } from "../../../config/translator";
import { getMainLeague } from "../../../config/utils";
import { pathLeagueView, pathPlayerView, pathTeamView } from "../../../config/routes";
import client, { callClientFunc } from "../../../client";
import ClavaTextInput from "../ClavaTextInput";
import { League, PlayerSearchElement, SearchResult, TeamListElement } from "../../../client/api";
import ListItem from "../ListItem";
import Loading from "../Loading";
import { selectLanguageOrDefault, selectUserValue } from "../../../selectors";

type SearchSectionType = "league" | "team" | "player";
type SearchSection = {
  title: SearchSectionType;
  data: League[] | TeamListElement[] | PlayerSearchElement[];
};

const ON_MORE_AMOUNT = 5;

function unique<T extends { id: number }>(arr: T[]): T[] {
  const seen = new Set<number>();
  return arr.filter(item => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

let searchTimeout: ReturnType<typeof setTimeout> | undefined;

const SearchDropdown: React.FC = () => {
  const l = useSelector(selectLanguageOrDefault);
  const user = useSelector(selectUserValue);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SearchResult | undefined>(undefined);
  const hasMore = useRef<{ team: boolean; league: boolean; player: boolean }>({
    team: false,
    league: false,
    player: false
  });
  const [moreLoading, setMoreLoading] = useState<{ team: boolean; league: boolean; player: boolean }>({
    team: false,
    league: false,
    player: false
  });

  const [hasScroll, setHasScroll] = useState(false);
  const [thumbHeight, setThumbHeight] = useState<string>("0px");
  const [thumbTop, setThumbTop] = useState<string>("0px");

  const regionId = useMemo(() => user?.areaOfInterest?.regionId, [user]);

  const onSearch = useCallback(
    (text: string) => {
      setQuery(text);
      const q = text.trim();
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      if (q.length > 2) {
        searchTimeout = setTimeout(() => {
          setLoading(true);
          callClientFunc(client().search, true, q, regionId, 5, 0)
            .then(res => {
              hasMore.current = {
                team: res.teams.length === 5,
                league: res.leagues.length === 5,
                player: res.players.length === 5
              };
              setResult(res);
            })
            .finally(() => {
              setLoading(false);
            });
          searchTimeout = undefined;
        }, 300);
      } else {
        setResult(undefined);
      }
    },
    [regionId]
  );

  const clearSearch = useCallback(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      searchTimeout = undefined;
    }
    setQuery("");
    setResult(undefined);
    setLoading(false);
    setMoreLoading({ team: false, league: false, player: false });
  }, []);

  const updateThumb = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    if (clientHeight === 0 || scrollHeight === 0 || scrollHeight <= clientHeight) {
      setHasScroll(false);
      setThumbHeight("0px");
      return;
    }
    setHasScroll(true);
    const ratio = clientHeight / scrollHeight;
    const height = Math.max(24, clientHeight * ratio);
    const maxTop = clientHeight - height;
    const top = (scrollTop / (scrollHeight - clientHeight)) * maxTop;
    setThumbHeight(`${height}px`);
    setThumbTop(`${top}px`);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (!containerRef.current) return;
      const target = event.target as Node | null;
      if (target && !containerRef.current.contains(target)) {
        clearSearch();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [clearSearch]);

  useEffect(() => {
    let frameId: number | null = null;

    if (loading) {
      setHasScroll(false);
      setThumbHeight("0px");
    } else {
      frameId = window.requestAnimationFrame(updateThumb);
    }

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [result, loading]);

  const sortedResults = useMemo<SearchSection[]>(() => {
    const res: SearchSection[] = [];
    if (!result) {
      return res;
    }
    if (result.leagues.length) {
      res.push({ title: "league", data: result.leagues });
    }
    if (result.teams.length) {
      res.push({ title: "team", data: result.teams });
    }
    if (result.players.length) {
      res.push({ title: "player", data: result.players });
    }
    return res;
  }, [result]);

  const totalResults = useMemo(() => {
    if (!result) return 0;
    return result.leagues.length + result.teams.length + result.players.length;
  }, [result]);

  const onClickTeam = useCallback((teamId: number) => {
    clearSearch();
    navigate(pathTeamView(teamId, "table"));
  }, [clearSearch, navigate]);

  const onClickLeague = useCallback((leagueId: number) => {
    clearSearch();
    navigate(pathLeagueView(leagueId, "table"));
  }, [clearSearch, navigate]);

  const onClickPlayer = useCallback((playerId: number) => {
    clearSearch();
    navigate(pathPlayerView(playerId));
  }, [clearSearch, navigate]);

  const onMore = useCallback(
    (offset: number, type: SearchSectionType) => {
      setMoreLoading(prev => ({ ...prev, [type]: true }));
      callClientFunc(
        client().searchExt,
        true,
        query,
        ON_MORE_AMOUNT,
        offset,
        type,
        undefined,
        regionId
      )
        .then(thisResult => {
          hasMore.current[type] =
            (type === "league"
              ? thisResult.leagues.length
              : type === "team"
              ? thisResult.teams.length
              : thisResult.players.length) === ON_MORE_AMOUNT;
          setResult(oldResult =>
            oldResult
              ? {
                  query: oldResult.query,
                  leagues: unique(oldResult.leagues.concat(thisResult.leagues)),
                  players: unique(oldResult.players.concat(thisResult.players)),
                  teams: unique(oldResult.teams.concat(thisResult.teams))
                }
              : thisResult
          );
        })
        .finally(() => {
          setMoreLoading(prev => ({ ...prev, [type]: false }));
        });
    },
    [regionId, query]
  );

  return (
    <div className="col-span-7 xl:col-span-6 relative" ref={containerRef}>
      <div className="w-full max-w-80">
        <ClavaTextInput
          placeholder={translate("search", l)}
          value={query}
          onChange={onSearch}
          iconL={faSearch}
        />
      </div>
      {(result || loading) && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50">
          <div className="bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark rounded-xl shadow-lg px-1 py-1">
            <div className="relative">
              <div
                ref={scrollRef}
                className={`max-h-[56vh] overflow-y-auto rounded-lg search-dropdown-scroll ${hasScroll ? "pr-3" : ""}`}
                onScroll={updateThumb}
              >
              {loading ? (
                <div className="p-4 flex items-center justify-center">
                  <Loading small />
                </div>
              ) : result && totalResults === 0 ? (
                <div className="p-4 text-center text-font-600 dark:text-font-600-dark text-sm">
                  {translate("noResults", l)}
                </div>
              ) : (
                <>
                  {sortedResults.map((section) => (
                    <div key={section.title} className="px-2 pb-1">
                      <div className="py-3 pl-3 pr-0 bg-bg dark:bg-bg-dark sticky top-0 flex items-center justify-between z-10 rounded-t-lg">
                        <span className="text-base font-medium text-font dark:text-font-dark">
                          {translate(section.title === "league" ? "leagues" : section.title === "team" ? "teams" : "players", l)}
                        </span>
                        {hasMore.current[section.title] && (
                          <button
                            type="button"
                            disabled={moreLoading[section.title]}
                            onClick={() => onMore(section.data.length, section.title)}
                            className={`text-xs px-2 py-1 rounded transition-colors ${
                              moreLoading[section.title]
                                ? "text-font-600 dark:text-font-600-dark opacity-50 cursor-wait"
                                : "text-primary hover:bg-primary-10 cursor-pointer"
                            }`}
                          >
                            {moreLoading[section.title] ? "..." : translate("more", l)}
                          </button>
                        )}
                      </div>
                      <div className="mt-1">
                        {section.title === "league" && (section.data as League[]).map((league) => (
                          <button
                            key={`league-${league.id}`}
                            type="button"
                            onClick={() => onClickLeague(league.id)}
                            className="w-full px-2 py-2 flex items-center gap-3 hover:bg-primary-50 dark:hover:bg-primary-50-dark transition-colors text-left rounded-lg"
                          >
                            <IconWithBackground
                              icon={faRankingStar}
                              className="w-5 h-5 text-bg-900 dark:text-bg-900-dark"
                              backgroundClassName="bg-input-bg dark:bg-bg-100-dark w-10 h-10"
                            />
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm font-medium text-font dark:text-font-dark truncate">
                                {showTranslated(league.name, l)}
                              </span>
                              <span className="text-xs font-normal text-font-600 dark:text-font-600-dark truncate">
                                {league.areasOfInterest?.map((aoiItem) => showTranslated(aoiItem.name, l)).join(" | ") || ""}
                              </span>
                            </div>
                          </button>
                        ))}
                        {section.title === "team" && (section.data as TeamListElement[]).map((team) => {
                          const mainLeague = getMainLeague(team.leagues);
                          return (
                            <ListItem
                              key={`team-${team.id}`}
                              image={team.emblem}
                              title={team.name}
                              subtitle={mainLeague?.name}
                              onClick={() => onClickTeam(team.id)}
                              l={l}
                            />
                          );
                        })}
                        {section.title === "player" && (section.data as PlayerSearchElement[]).map((player) => (
                          <ListItem
                            key={`player-${player.id}`}
                            playerPhoto={player.photo}
                            image={player.mainTeam?.emblem}
                            title={`${player.givenName} ${player.familyName}`}
                            subtitle={player.mainTeam?.name}
                            onClick={() => onClickPlayer(player.id)}
                            l={l}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
              </div>

              {!loading && hasScroll && (
                <div className="pointer-events-none absolute inset-y-1 right-1 w-2 flex z-20">
                  <div className="w-full rounded-full bg-black/5 dark:bg-white/10">
                    <div
                      className="pointer-events-auto w-full rounded-full bg-primary"
                      style={{ height: thumbHeight, marginTop: thumbTop }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;

